import { useCallback, useContext, useEffect, useState } from 'react';

// Contexts
import MultisportApiContext from '../contexts/MultisportApiContext';

import { IMultisportCompetitionEvents, IMultisportEventsConfig, ISubscriberFn, SportTypes } from '../../../../packages/types';
import useSubscriptionClick from '../../../../packages/hooks/useSubscriptionClick';
import { IGeneralSubscriberInfo } from '../../../../packages/constants/subscribeTypes';
import useFetchIntervalData from '../../../../packages/hooks/useFetchIntervalData';
import { stackOdds } from '@sportal365/smp-widget-utils/src/oddsCounter';
import { modifyNameTypeForSdk } from './useEvents';

interface IUseCompetitionEventsArgs extends IGeneralSubscriberInfo {
  date: string;
  sport?: SportTypes | null;
  dataWidgetSport?: string;
  dataWidgetId?: string;
  dataWidgetCuid?: string;
  subscribers?: { [key: string]: ISubscriberFn[] };
  dataOddsDisplay: IMultisportEventsConfig['dataOddsDisplay'];
  dataOddsBettingId: IMultisportEventsConfig['dataOddsBettingId'];
  dataOddsPreEventOnly: IMultisportEventsConfig['dataOddsPreEventOnly'];
  competitionIds?: IMultisportEventsConfig['dataCompetitions'];
  dataTeamNameType: IMultisportEventsConfig['dataTeamNameType'];
  dataRefreshTime?: IMultisportEventsConfig['dataRefreshTime'];
  dataLimit?: IMultisportEventsConfig['dataLimit'];
}

export default function useCompetitionEvents({
  date,
  sport,
  competitionIds,
  dataOddsDisplay,
  dataOddsBettingId,
  dataOddsPreEventOnly,
  dataWidgetSport,
  dataWidgetCuid,
  dataWidgetId,
  subscribers,
  dataRefreshTime,
  dataTeamNameType,
  dataLimit,
}: IUseCompetitionEventsArgs) {
  const { $multisport } = useContext(MultisportApiContext);
  const [competitionEvents, setCompetitionEvents] = useState<IMultisportCompetitionEvents>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const hasCompetitions = Boolean(competitionIds?.length && competitionIds.length > 0);

  const fetchCompetitionEvents = useCallback(
    (manageLoader: boolean, isFromInterval = false) => {
      if (sport || hasCompetitions) {
        manageLoader && setIsLoading(true);
        $multisport
          ?.competitionEvents({
            sport,
            competitionIds,
            nameType: modifyNameTypeForSdk(dataTeamNameType),
            date,
            limit: dataLimit,
            dataOddsDisplay,
            dataOddsBettingId,
            dataOddsPreEventOnly,
          })
          ?.then((res) => {
            manageLoader && setIsLoading(false);
            if (res) {
              setCompetitionEvents(res);
              setErrorMessage(null);
              !isFromInterval && stackOdds(res, dataWidgetSport, dataWidgetId, dataWidgetCuid, dataOddsBettingId);
            }
          })
          .catch((error) => {
            manageLoader && setIsLoading(false);
            console.error('Error fetching competition events:', error.message);

            if (error.message && error.message.includes('competitionIds') && !sport) {
              setErrorMessage('Invalid competition id/s were sent');
            } else {
              setErrorMessage(`Failed to fetch events: ${error.message}`);
            }
          });
      }
    },
    [$multisport, sport, date, competitionIds, dataLimit, dataTeamNameType, dataOddsDisplay, dataOddsBettingId, dataOddsPreEventOnly],
  );

  const refetchWidgetData = useFetchIntervalData(fetchCompetitionEvents, dataRefreshTime);

  useEffect(() => {
    fetchCompetitionEvents(true);
  }, [fetchCompetitionEvents]);

  return {
    competitionEvents,
    refetchWidgetData,
    handleClick: useSubscriptionClick({ dataWidgetId, dataWidgetSport, dataWidgetCuid, subscribers }),
    isLoading,
    errorMessage,
  };
}
